import { SortDirection } from 'src/app/services/api.services';
import { isNullOrUndefined } from 'src/app/shared/utils';
import { Visual } from './Visual';
import { VisualField } from './VisualField';

export class VisualFieldSorting {
  constructor(
    public readonly visual: Visual,
    public readonly options?: VisualFieldSorting.Options) { }

  readonly maxCount? = this.options?.maxCount ?? 0;
  readonly required = !!this.options?.required;
  readonly dontAllowGroupings = !!this.options?.dontAllowGroupings;
  readonly dontAllowMeasures = !!this.options?.dontAllowMeasures;

  canSetSort(field: VisualField): VisualFieldSortChange {
    if (this.maxCount < 1)
      return VisualFieldSortChange.NotAllowed;

    if (!field.role())
      return VisualFieldSortChange.NotAllowed;

    if (!field.role().type.sortable(this.visual))
      return VisualFieldSortChange.NotAllowed;

    if (field.isAggregated) {
      if (this.dontAllowMeasures)
        return VisualFieldSortChange.NotAllowed;
    }
    else {
      if (this.dontAllowGroupings)
        return VisualFieldSortChange.NotAllowed;
    }

    if (!isNullOrUndefined(this.maxCount)) {
      if (this.visual.fields.count(f => f.sorted() && f != field) >= this.maxCount)
        return VisualFieldSortChange.Replace;
    }

    return VisualFieldSortChange.Add;
  }

  canUnsetSort(field: VisualField): boolean {
    if (this.maxCount < 1)
      return true;

    if (!this.required)
      return true;

    if (this.visual.fields.count(f => f.sorted() && f != field) > 0)
      return true;

    return false;
  }

  setSortDirection(field: VisualField, sortDirection: SortDirection): boolean {
    return this.setSortingOf(field, sortDirection, field.sortOrder());
  }

  setSortingOf(field: VisualField, sortDirection: SortDirection | null, sortOrder?: number): boolean {
    if (!!sortDirection) {
      let canSetSort = this.canSetSort(field);
      if (canSetSort == VisualFieldSortChange.NotAllowed)
        return false;

      if (canSetSort == VisualFieldSortChange.Replace) {
        let other = this.visual.fields
          .filter(f => f != field && f.sorted())
          .sort((a, b) => {
            if (a.role() == b.role())
              return -1;

            return b.sortOrder() - a.sortOrder();
          });

        let anyUnset = false;

        for (let f of other) {
          if (this.setSortingOf(f, null)) {
            anyUnset = true;
            break;
          }
        }

        if (!anyUnset)
          return false;
      }

      field.sorted.set(true);
      field.sortOrder.set(sortOrder);
      field.sortDirection.set(sortDirection);
    }
    else {
      if (!this.canUnsetSort(field))
        return false;

      field.sorted.set(false);
      field.sortOrder.set(0);
      field.sortDirection.set(null);
    }

    return true;
  }

  unsetAllSort() {
    [...this.visual.fields.all()].forEach(f => {
      if (this.canUnsetSort(f)) {
        f.sorted.set(false);
        f.sortOrder.set(0);
        f.sortDirection.set(null);
      }
    });
  }

  sortedFieldCount() {
    return this.visual.fields.filter(x => x.sorted()).length;
  }

  maxFieldSortOrder() {
    return this.visual.fields.filter(x => x.sorted()).reduce((n, f) => Math.max(n, f.sortOrder() ?? 0), 0);
  }

  getOrderedSortList() {
    return this.visual.fields
      .filter(x => x.sorted())
      .sort((a, b) => a.sortOrder() - b.sortOrder());
  }
}

export enum VisualFieldSortChange {
  NotAllowed,
  Replace,
  Add
}

export namespace VisualFieldSorting {
  export interface Options {
    readonly maxCount?: number;
    readonly required?: boolean;
    readonly dontAllowGroupings?: boolean;
    readonly dontAllowMeasures?: boolean;
  }

  export function none() {
    return v => new VisualFieldSorting(v, { maxCount: 0 });
  }

  export function single(options?: Options) {
    return v => new VisualFieldSorting(v, { ...options, maxCount: 1 });
  }

  export function upTo(maxCount: number, options?: Options) {
    return v => new VisualFieldSorting(v, { ...options, maxCount });
  }

  export function any(options?: Options) {
    return v => new VisualFieldSorting(v, { maxCount: 9999, ...options });
  }
}
